import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatColorInput, getColorConfigs, isValidHexColor } from '../utils';
import { StyledInputLabel } from '../../../../../components/inputs/InputLabel';
import { StyledQuestionMark } from '../../../../../components/QuestionMark';

export const ColorPicker = memo(
  ({
    config,
    value,
    onChange,
    disabled,
    required = false,
  }: {
    config: ReturnType<typeof getColorConfigs>[number];
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    required?: boolean;
  }) => {
    const { t } = useTranslation();
    const currentValue = value;
    const isValid = isValidHexColor(currentValue);
    const id = `color_${config.key}`;

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatColorInput(e.target.value);
      onChange(formatted);
    };

    return (
      <Box sx={{ my: 14 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StyledInputLabel htmlFor={id}>{config.label}</StyledInputLabel>
          <Tooltip title={config.tooltip} placement="top">
            <IconButton
              size="small"
              aria-label={config.tooltip}
            >
              <StyledQuestionMark fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          id={id}
          value={currentValue}
          onChange={handleTextChange}
          error={!isValid}
          helperText={!isValid ? t('branding.errors.enterValidHexColor') : ''}
          required={required}
          sx={{
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'var(--color-grey-red)',
              },
            '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'var(--color-dark-grey-red)',
              },
            '& .MuiFormHelperText-root.Mui-error': {
              color: 'var(--color-grey-red)',
              fontWeight: 600,
            },
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 1,
                    '&:focus-within': {
                      outline: '2px solid var(--color-scifree-teal)',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 24,
                      borderRadius: 0.5,
                      border: 1,
                      borderColor: isValid
                        ? 'var(--color-dark-greyish-blue)'
                        : 'var(--color-grey-red)',
                      backgroundColor: isValid
                        ? currentValue
                        : 'var(--color-white)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      marginRight: 16,
                    }}
                  />
                  <TextField
                    type="color"
                    value={isValid ? currentValue : '#000000'}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    inputProps={{
                      'aria-label': config.label,
                      tabIndex: disabled ? -1 : 0,
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  />
                </Box>
              </Box>
            ),
          }}
          fullWidth
          margin="none"
          disabled={disabled}
          placeholder="#000000"
        />
      </Box>
    );
  }
);

ColorPicker.displayName = 'ColorPicker';
